import React, {useRef} from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {useTypedSelector} from "helpers/reducers";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import useCustomTimeslotsNew from "utils/hooks/useCustomTimeslotsNew";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {getCategoryTypes} from "pages/Front/Booking/Competitions/competitionUtils";
import CompetitionsSection from "pages/Front/Booking/Competitions/CompetitionsSection";
import {getShortNameFromId} from "utils/reservationUtils";
import useBookingPackagesView from "pages/Front/Booking/components/BookingPackageNew/hooks/useBookingPackagesView";
import {useCompetitions} from "utils/hooks/useCompetitions";
import RenounceCompetitionItem from "pages/Front/Booking/Competitions/components/RenounceCompetitionItem";
import BookingPackagesViewFooter from "pages/Front/Booking/components/BookingPackageNew/components/BookingPackagesViewFooter";
import BookingDateHeaderNamed from "pages/Front/Booking/components/BookingPackageNew/components/BookingDateHeaderNamed";

export interface Params {}

interface BookingCompetitionsProps extends RouteComponentProps<Params> {}

const BookingCompetitions = (props: BookingCompetitionsProps) => {
    const {t} = useTranslation();
    const {filter, selectedTimeslot, selectedPackageCreditItemsQuantity} = useBookingPackagesView(props.history);
    const {freeCompetitionsCount, selectedFreeCompetitionsCount, renounceFreeCompetitions, freeSelectCompetitions, paidCompetitions} = useCompetitions();
    const bookingState = useTypedSelector(state => state.bookingState);
    const {historyPush, handleRouteNavigation} = useNavigation();

    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);
    const freeCompetitionsRef = useRef<HTMLDivElement>(null);
    const {isLoading} = useCustomTimeslotsNew(() => {}, props.history, [scrollRefTimeslots, scrollRefCustomTimeslots, scrollRefConfirmTimeslots]);
    const selectedPackageName = filter.category ? getShortNameFromId(filter.category) : undefined;

    const isMissingFreeCompetitions = selectedFreeCompetitionsCount < freeCompetitionsCount && !renounceFreeCompetitions;
    const isContinueDisabled = isMissingFreeCompetitions;

    const continueLabel = isMissingFreeCompetitions
        ? freeCompetitionsCount - selectedFreeCompetitionsCount > 1
            ? t("Choose {{count}} more competitions", {count: freeCompetitionsCount - selectedFreeCompetitionsCount})
            : t("Choose one more competition")
        : undefined;

    const continueDescription = isMissingFreeCompetitions
        ? t("You still have {{count}} inclusive competition to choose — or to waive them", {
              count: freeCompetitionsCount - selectedFreeCompetitionsCount,
          })
        : undefined;

    const selectedCompetitions = useTypedSelector(state => state.bookingState.bookingProcess.competition);
    // const freeSelectSubtitle = [
    //     t("Included in your package"),
    //     t("Choose {{amount}} from category {{category}}", {
    //         amount: freeCompetitionsCount,
    //         category: getCategoryTypes(freeSelectCompetitions).join(", "),
    //     }),
    //     t("for the whole group"),
    // ].join(" · ");
    const freeSelectSubtitle = (
        <div className={"text-sm font-bold gap-x-2 flex flex-row flex-wrap items-center"}>
            <span className={"whitespace-nowrap"}>{t("Included in your package")}</span>
            <span className={"hidden xs:block"}>·</span>
            <span className={"font-semibold"}>
                {t("Choose {{amount}} from category {{category}}", {
                    amount: freeCompetitionsCount,
                    category: getCategoryTypes(freeSelectCompetitions).join(t(" or ")),
                })}
            </span>
            <span className={"hidden xs:block"}>·</span>
            <span className={"font-semibold whitespace-nowrap"}>{t("for the whole group")}</span>
        </div>
    );

    // back button
    const onBack = () => {
        return handleRouteNavigation();
    };

    // continue button
    const onContinue = () => {
        if (isMissingFreeCompetitions) {
            freeCompetitionsRef.current?.scrollIntoView({behavior: "smooth"});
            return;
        }

        const packageCart = bookingState?.bookingProcess?.packageCart;
        if (!packageCart || !bookingState.bookingProcess.selectedTimeslot) {
            toast.error(t("No package found"));
            return;
        }

        // if (IS_UPSELL_ALLOWED) {
        return historyPush(RouteName.UpsellsNew, "Packages");
        // }
        // historyPush(RouteName.Customer, "Packages");
    };

    return (
        <div className={"BookingCompetitions w-full border bg-white rounded-xl shadow"}>
            <div
                className={
                    "BookingCompetitionsViewHeader grid grid-cols-1 xs:grid-cols-2 lg:flex lg:flex-row items-center rounded-t-xl w-full bg-[#F5F5F566]/40 border-b border-[#D9D9D9] px-4 py-4 lg:px-3 xl:px-4 lg:py-3 xl:py-4 gap-y-2 gap-x-4"
                }
            >
                <div className="flex items-center gap-2 xl:gap-3 order-1">
                    <h2 className={"font-bold text-xl xl:text-2xl"}>{selectedPackageName}</h2>
                </div>

                <div className={"w-px h-8 bg-[#D9D9D9] hidden lg:block order-2"} />

                <div className="flex flex-col md:flex-row items-start xs:items-end md:items-center gap-0 md:gap-3 order-3 sm:justify-self-end lg:justify-self-auto">
                    <span className={"font-semibold text-base shrink-0"}>
                        {filter.children + filter.adults} {t("persons")}
                    </span>
                    {filter?.children != 0 && (
                        <span className={"text-base font-semibold leading-tight"}>
                            ({filter.adults} {t("Adults")}, {filter.children} {t("Children")})
                        </span>
                    )}
                </div>

                <div className={"w-px h-8 bg-[#D9D9D9] hidden lg:block order-4"} />

                <div className={"flex items-center gap-2 order-5"}>
                    <BookingDateHeaderNamed date={filter.date} />
                </div>

                <div className="sm:ml-auto order-6 xs:justify-self-end lg:justify-self-auto flex flex-row items-center gap-4">
                    <img src="/img/credits/arbo-credits-icon.svg" alt="credits icon" className="size-[30px] flex-none" />
                    <span className={"font-medium text-base text-black"}>
                        {selectedPackageCreditItemsQuantity} {t("people with credits")}
                    </span>
                </div>
            </div>

            {/*<div className={"flex flex-col gap-y-8 sm:gap-y-12 w-full"}>*/}
            <div className={"flex flex-col gap-y-8 px-4 py-4 lg:px-3 xl:px-4 lg:py-3 xl:py-4 w-full"}>
                {freeCompetitionsCount !== 0 && (
                    <div ref={freeCompetitionsRef}>
                        <CompetitionsSection
                            badge={t("Inclusive")}
                            badgeColor="green"
                            competitions={freeSelectCompetitions}
                            counter={
                                renounceFreeCompetitions
                                    ? selectedFreeCompetitionsCount > 0
                                        ? t("{{selected}} selected + Renunciation", {selected: selectedFreeCompetitionsCount})
                                        : t("Renunciation chosen")
                                    : t("{{selected}} of {{total}} selected", {
                                          selected: selectedFreeCompetitionsCount,
                                          total: freeCompetitionsCount,
                                      })
                            }
                            isCounterComplete={!isMissingFreeCompetitions}
                            subtitle={freeSelectSubtitle}
                            title={t("Competitions")}
                        >
                            <RenounceCompetitionItem />
                        </CompetitionsSection>
                    </div>
                )}

                <CompetitionsSection
                    badge={t("Optional · with costs")}
                    competitions={paidCompetitions}
                    title={freeCompetitionsCount == 0 ? t("Competitions") : t("More Competitions")}
                />

                {freeSelectCompetitions.length === 0 && paidCompetitions.length === 0 && (
                    <div className={"rounded-xl bg-white shadow w-full px-4 py-6 text-center text-base font-medium"}>{t("No competitions available")}</div>
                )}
            </div>

            <BookingPackagesViewFooter
                onBack={onBack}
                onContinue={onContinue}
                disabled={isContinueDisabled}
                continueLabel={continueLabel}
                continueDescription={continueDescription}
            />
        </div>
    );
};

export default withRouter(BookingCompetitions);
